import React, { useState, useEffect, useRef } from 'react';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { useLearning } from '../../store/LearningContext';
import i18n from '../../i18n';
import Icon from '../../icons/Icon';
import './LearningTooltip.scss';

export const LearningTooltip = ({ 
    id, 
    children, 
    content,
    placement = 'top', 
    delay = 2000,
    className = '',
    style = {}
}) => {
    const { shouldShowTooltip, markTooltipAsSeen } = useLearning();
    const [show, setShow] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const timerRef = useRef(null);

    useEffect(() => {
        if (shouldShowTooltip(id)) {
            setShow(true);
            timerRef.current = setTimeout(() => {
                setShow(false);
                markTooltipAsSeen(id);
            }, delay);
            
            return () => {
                if (timerRef.current) {
                    clearTimeout(timerRef.current);
                }
            };
        }
    }, [id, markTooltipAsSeen, delay]);

    const handleSettingsClick = (e) => {
        e.stopPropagation();
        setShowSettings(!showSettings);
        
        // Clear the timer when settings are opened
        if (!showSettings && timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    };

    const handleSettingsChange = () => {
        // Reset the delay timer when settings change
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        
        // Start a new timer
        timerRef.current = setTimeout(() => {
            setShow(false);
            markTooltipAsSeen(id);
        }, delay);
    };

    style = {}
    if(placement === 'right'){
        style = {
            left: '100%',
            top: '50%',
            transform: 'translateY(-50%)'
        }
    }

    const popover = (
        <Popover id={`popover-${id}`} style={{ zIndex: 9999, borderRadius: '8px', boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)' }}>
            <div className="learning-popover d-flex flex-column py-2 px-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                <div className="d-flex justify-content-between align-items-center mb-1">
                    <p className="title-small mb-0" style={{ fontWeight: 'bold', color: '#343a40' }}>Learning Tip</p>
                    <button 
                        className="btn btn-sm btn-outline-secondary"
                        onClick={handleSettingsClick}
                        style={{ padding: '2px 6px', fontSize: '10px' }}
                        title="Toggle Learning Settings"
                    >
                        <Icon name="ICON_SETTINGS" />
                    </button>
                </div>
                
                {showSettings ? (
                    <LearningTooltipSettings onSettingsChange={handleSettingsChange} />
                ) : (
                    <p className="small mb-0" style={{ color: '#495057' }}>{content}</p>
                )}
            </div>
        </Popover>
    );

    return (
        <OverlayTrigger
            placement={placement}
            show={show}
            trigger={[]}
            overlay={popover}
        >
            {children}
        </OverlayTrigger>
    );
};

// Settings component for controlling tooltip behavior
export const LearningTooltipSettings = ({ onSettingsChange }) => {
    const { settings, updateSettings, resetAllSettings } = useLearning();

    const handleModeChange = (e) => {
        updateSettings({ mode: e.target.value });
        onSettingsChange && onSettingsChange();
    };

    const handleCountChange = (e) => {
        updateSettings({ showCount: parseInt(e.target.value) });
        onSettingsChange && onSettingsChange();
    };

    return (
            <div className="d-flex align-items-center text-white" style={{ margin: '10px 0', height: '40px' }}>
                <select 
                    id="tooltip-mode"
                    className="form-select  my-1"
                    value={settings.mode} 
                    onChange={handleModeChange}
                    style={{ marginLeft: '', padding: '4px', height: '100%' }}
                >
                    <option value="always">{i18n.t('Always show explainer')}</option>
                    <option value="show_on_x_times">{i18n.t('Show on x times')}</option>
                    <option value="never">{i18n.t('Never show')}</option>
                </select>
                {settings.mode === 'show_on_x_times' && (
                    <input 
                        id="tooltip-count"
                        className="form-control text-white"
                        type="number" 
                        min="1" 
                        value={settings.showCount} 
                        onChange={handleCountChange}
                        style={{ marginLeft: '10px', padding: '0 4px', width: '60px', height: '100%'  }}
                    />
                )}
            </div>
    );
};